import * as THREE from 'three';
import { createScene } from './core/threeCoreHandler';
import { createRenderer } from './core/threeCoreHandler';
import { createCamera } from './core/threeCoreHandler';

import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import RAPIER from '@dimforge/rapier3d-compat';
import { RapierDebugRenderer } from '../rapier/helper/RapierDebugRenderer';
import { SETTINGS } from './SETTINGS';

// Models
import { Ground } from './world/Ground';
import { Sphere, PhysicsSphere } from './world/Sphere';
import { Cube } from './world/Cube';

export class WebGlApp {
  // Flag
  private controlFlag = false; // カメラコントロールの有効化
  private debugFlag = false; // 物理演算のデバッグ表示の有効化
  // Canvas
  private canvas: HTMLCanvasElement;
  private canvasWidth: number;
  private canvasHeight: number;
  private canvasPixelRatio: number;
  // Three Init
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private controls: OrbitControls | null = null;
  private loader: GLTFLoader;
  // Models
  private assetsBase: string;
  private spheres: PhysicsSphere[] = [];
  private sphereManager: Sphere | null = null;
  private ground: Ground;
  // Physics
  private world: RAPIER.World | null = null;
  private debugRenderer: RapierDebugRenderer | null = null;
  private enablePhysicsDebug = false;
  // Interaction
  private raycaster: THREE.Raycaster;
  private mouse: THREE.Vector2 = new THREE.Vector2();

  constructor(canvas: HTMLCanvasElement, world?: RAPIER.World) {
    this.canvas = canvas;
    this.canvasWidth = canvas.getBoundingClientRect().width;
    this.canvasHeight = canvas.getBoundingClientRect().height;
    this.canvasPixelRatio = Math.min(window.devicePixelRatio, 2);

    this.assetsBase = (canvas.dataset.assetsBase || "").replace(/\/$/, "");
    this.world = world || null;
    this.enablePhysicsDebug = Boolean(SETTINGS.debug?.physics);

    // Three.js setup
    this.scene = createScene();
    this.camera = createCamera(this.canvasWidth / this.canvasHeight);
    this.renderer = createRenderer(canvas);
    this.renderer.setSize(this.canvasWidth, this.canvasHeight, false);
    this.renderer.setPixelRatio(this.canvasPixelRatio);

    this.setupLight();

    if (this.controlFlag) {
      this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    }


    this.loader = new GLTFLoader();

    // Create Objects
    // 物理演算対応
    this.ground = new Ground(this.assetsBase, this.loader, this.scene, this.world || undefined);

    // 球体
    this.sphereManager = new Sphere(this.scene, this.assetsBase, this.world || undefined);
    this.spheres = this.sphereManager.getSpheres();

    if (this.enablePhysicsDebug && this.debugFlag) {
      this.debugRenderer = new RapierDebugRenderer(this.scene);
      this.debugRenderer.setWorld(this.world);
    }

    // Interaction
    this.raycaster = new THREE.Raycaster();

    // クリックで球体を追加
    canvas.addEventListener('click', () => {
      this.createFallingSphere();
    });
  }

  // ------------------------ Public -------------------------
  /**
   * 物理演算ワールドを設定
   */
  public setWorld = (world: RAPIER.World): void => {
    this.world = world;
    // 球体マネージャーにワールドを設定
    if (this.sphereManager) {
      this.sphereManager.setWorld(world);
    }
    this.ground.setWorld(world);
    if (this.debugRenderer) {
      this.debugRenderer.setWorld(world);
    }
    // 物理演算ワールドが設定された後に初期球体を生成
    setTimeout(() => {
      this.createInitialSpheres();
    }, 1000);
  }

  /**
   * AppHandlerによってRAFごとに呼び出される関数
   */
  public render = (): void => {
    this.update();
    if (this.controlFlag && this.controls) {
      this.controls.update();
    }
    this.renderer.render(this.scene, this.camera);
  }
  public update = (): void => {
    const elapsedTime = performance.now();

    // 物理演算の更新
    if (this.world) {
      this.world.step();
      if (this.enablePhysicsDebug && this.debugRenderer) {
        this.debugRenderer.render();
      }
    }

    // 球体の位置を物理演算から同期（Sphereクラスのupdateメソッドを使用）
    if (this.sphereManager) {
      this.sphereManager.update();
    }
  }


  // ------------------------ Private ------------------------
  private setupLight = () => {
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    this.scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xefefef, 4);
    directionalLight.position.set(1, 5, 1);
    this.scene.add(directionalLight);
  }

  /**
   * 初期球体を作成
   */
  private createInitialSpheres = (): void => {
    if (!this.world || !this.sphereManager) {
      return;
    }

    // 3つの球体を上から落とす
    for (let i = 0; i < 3; i++) {
      setTimeout(() => {
        this.sphereManager!.createFallingSphere();
        this.spheres = this.sphereManager!.getSpheres();
      }, i * 100); // 1秒間隔で生成
    }
  }

  /**
   * 球体を作成して上から落とす
   */
  private createFallingSphere = (): void => {
    if (!this.world || !this.sphereManager) {
      return;
    }

    this.sphereManager.createFallingSphere();
    this.spheres = this.sphereManager.getSpheres();
  }
}
